import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  PackageCheck, 
  ShoppingCart, 
  Truck, 
  CheckCircle2, 
  Plus, 
  Search, 
  Eye, 
  Trash2,
  Loader2,
  Calendar,
  Package,
  Building,
  Clock,
  CheckCircle,
  Upload,
  FileText,
  X,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Layers,
  Download,
  ClipboardList,
  DollarSign,
  CreditCard,
  UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PurchasingPurchased = ({ readOnly = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('ordered');
  const [orders, setOrders] = useState([]);
  const [arrivedProducts, setArrivedProducts] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [defectiveProducts, setDefectiveProducts] = useState([]);
  const [cancelledDefective, setCancelledDefective] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [branches, setBranches] = useState([]);
  const [stockCards, setStockCards] = useState([]);
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [expandedDefective, setExpandedDefective] = useState({});
  const [expandedCancelled, setExpandedCancelled] = useState({});
  
  // Bulk selection states
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  
  // Pending job from PurchasingJobs page (for "Tamamla" button)
  const [pendingJobData, setPendingJobData] = useState(null);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'order_date', direction: 'desc' });
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDemirbasModal, setShowDemirbasModal] = useState(false);
  
  // Cancel and Order modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCompleteOrderModal, setShowCompleteOrderModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingItem, setCancellingItem] = useState(null);
  const [completeOrderItem, setCompleteOrderItem] = useState(null);
  const [completeOrderQuantities, setCompleteOrderQuantities] = useState({});
  const [completeOrderCariId, setCompleteOrderCariId] = useState('');
  const [completeOrderDate, setCompleteOrderDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Source job ID (when coming from "Tamamla" button)
  const [sourceJobId, setSourceJobId] = useState(null);
  const sourceJobIdRef = useRef(null); // Ref ile de tut - state kaybolabilir
  const jobDataProcessedRef = useRef(false);
  
  // Form state - Updated to match PurchasingWarehouses.js Stok Ekle modal
  const [formData, setFormData] = useState({
    target_branch_id: '',
    cari_hesap_id: '',
    entry_date: '',
    entry_time: '',
    estimated_arrival_date: '',
    estimated_arrival_time: '',
    currency: 'TL',
    items: [{ stok_karti_id: '', product_name: '', model: '', quantity: '', unit: 'adet', purchase_price: '', kdv_orani: 20 }],
    documents: [],
    description: '',
    nakliye_var: false,
    nakliye_ucreti: '',
    payment_plan: []
  });

  // Demirbaş form state
  const [demirbasFormData, setDemirbasFormData] = useState({
    product_name: '',
    quantity: 1,
    unit: 'adet',
    purchase_price: 0,
    description: '',
    serial_number: '',
    supplier_id: '',
    supplier: '',
    brand: '',
    status: 'active',
    documents: [],
    invoice_documents: []
  });
  const [demirbasCariList, setDemirbasCariList] = useState([]);

  // Search states for autocomplete
  const [cariSearchInput, setCariSearchInput] = useState('');
  const [filteredCari, setFilteredCari] = useState([]);
  const [showCariDropdown, setShowCariDropdown] = useState(false);
  const [selectedCariName, setSelectedCariName] = useState('');
  const [stokSearchInputs, setStokSearchInputs] = useState(['']);
  const [filteredStokList, setFilteredStokList] = useState([[]]);
  const [showStokDropdowns, setShowStokDropdowns] = useState([false]);

  const token = localStorage.getItem('fortis_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Check if order is unread by current user
  const isUnread = useCallback((order) => {
    if (!user?.id) return false;
    const readBy = order.read_by || [];
    return !readBy.includes(user.id);
  }, [user?.id]);

  // Mark single order as read
  const markAsRead = useCallback(async (orderId) => {
    if (!user?.id) return;
    try {
      await fetch(`${API_URL}/api/purchasing/orders/${orderId}/mark-read`, {
        method: 'POST',
        headers
      });
      // Update local state for all order arrays
      const updateReadBy = (orderList) => orderList.map(o => {
        if (o.id === orderId) {
          return { ...o, read_by: [...(o.read_by || []), user.id] };
        }
        return o;
      });
      setOrders(updateReadBy);
      setArrivedProducts(updateReadBy);
      setCompletedOrders(updateReadBy);
      setDefectiveProducts(updateReadBy);
      setCancelledDefective(updateReadBy);
    } catch (err) {
      console.error('Okundu işaretleme hatası:', err);
    }
  }, [user?.id, headers]);

  // Unread counts per category
  const unreadCounts = useMemo(() => {
    if (!user?.id) return { ordered: 0, arrived: 0, completed: 0, defective: 0, cancelled: 0 };
    
    const countUnread = (orderList) => orderList.filter(order => {
      const readBy = order.read_by || [];
      return !readBy.includes(user.id);
    }).length;
    
    return {
      ordered: countUnread(orders),
      arrived: countUnread(arrivedProducts),
      completed: countUnread(completedOrders),
      defective: countUnread(defectiveProducts),
      cancelled: countUnread(cancelledDefective)
    };
  }, [user?.id, orders, arrivedProducts, completedOrders, defectiveProducts, cancelledDefective]);

  // Bulk selection functions
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleSelectAll = (items) => {
    const itemIds = items.map(i => i.id);
    const allSelected = itemIds.every(id => selectedItems.includes(id));
    
    if (allSelected) {
      setSelectedItems(prev => prev.filter(id => !itemIds.includes(id)));
    } else {
      setSelectedItems(prev => [...new Set([...prev, ...itemIds])]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error('Lütfen silmek için kayıt seçin');
      return;
    }
    
    if (!window.confirm(`${selectedItems.length} kaydı kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`)) {
      return;
    }
    
    setBulkDeleteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/purchasing/orders/bulk-delete`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ order_ids: selectedItems })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.deleted_count || selectedItems.length} kayıt silindi`);
        setSelectedItems([]);
        // Refresh all data
        fetchOrders();
        fetchArrivedProducts();
        fetchCompletedOrders();
        fetchDefectiveProducts();
        fetchCancelledDefective();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Kayıtlar silinemedi');
      }
    } catch (error) {
      console.error('Toplu silme hatası:', error);
      toast.error('Kayıtlar silinirken hata oluştu');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Fetch data
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/purchasing/orders?status=ordered`, { headers });
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchArrivedProducts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/purchasing/orders?status=arrived`, { headers });
      if (response.ok) {
        const data = await response.json();
        setArrivedProducts(data);
      }
    } catch (error) {
      console.error('Gelen ürünler yüklenemedi:', error);
    }
  }, []);

  const fetchCompletedOrders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/purchasing/orders?status=completed`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCompletedOrders(data);
      }
    } catch (error) {
      console.error('Tamamlanan siparişler yüklenemedi:', error);
    }
  }, []);

  const fetchDefectiveProducts = useCallback(async () => {
    try {
      // Fetch only 'acik' status - truly defective items waiting for action
      const response = await fetch(`${API_URL}/api/purchasing/hatali-urunler?group_by_order=true&status=acik`, { headers });
      if (response.ok) {
        const data = await response.json();
        setDefectiveProducts(data);
      }
    } catch (error) {
      console.error('Hatalı/Eksik ürünler yüklenemedi:', error);
    }
  }, []);

  const fetchCancelledDefective = useCallback(async () => {
    try {
      // Fetch cancelled/iptal items grouped by order
      const response = await fetch(`${API_URL}/api/purchasing/hatali-urunler?group_by_order=true&status=iptal`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCancelledDefective(data);
      }
    } catch (error) {
      console.error('İptal edilen ürünler yüklenemedi:', error);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/branches`, { headers });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Şubeler yüklenemedi:', error);
    }
  }, []);

  const fetchStockCards = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/global/stok-kartlari`, { headers });
      if (response.ok) {
        const data = await response.json();
        setStockCards(data);
        setFilteredStokList([data]);
      }
    } catch (error) {
      console.error('Stok kartları yüklenemedi:', error);
    }
  }, []);

  const fetchCariHesaplar = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/global/cari-hesaplar`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCariHesaplar(data);
        setFilteredCari(data);
      }
    } catch (error) {
      console.error('Cari hesaplar yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchArrivedProducts();
    fetchCompletedOrders();
    fetchDefectiveProducts();
    fetchCancelledDefective();
    fetchBranches();
    fetchStockCards();
    fetchCariHesaplar();
  }, [fetchOrders, fetchArrivedProducts, fetchCompletedOrders, fetchDefectiveProducts, fetchCancelledDefective, fetchBranches, fetchStockCards, fetchCariHesaplar]);

  // Check for pending job from PurchasingJobs page (Tamamla button redirect)
  useEffect(() => {
    // window.location.search kullan - daha güvenilir
    const searchParams = new URLSearchParams(window.location.search);
    const shouldOpenModal = searchParams.get('openAddModal') === 'true';
    const urlJobId = searchParams.get('jobId');
    const urlJobName = searchParams.get('jobName');
    
    // localStorage'dan da kontrol et
    const storedJobId = localStorage.getItem('draft_source_job_id');
    const storedJobName = localStorage.getItem('draft_source_job_name');
    
    const finalJobId = urlJobId || storedJobId;
    const finalJobName = urlJobName || storedJobName;
    
    if (shouldOpenModal || storedJobId) {
      if (jobDataProcessedRef.current) return; // Zaten işlendi, tekrar çalışma
      jobDataProcessedRef.current = true;
      
      // URL'den veya localStorage'dan jobId varsa
      if (finalJobId) {
        setSourceJobId(finalJobId);
        sourceJobIdRef.current = finalJobId; // Ref'e de kaydet
        
        // localStorage'dan iş verisini oku
        const jobDataStr = localStorage.getItem('draft_source_job_data');
        let jobData = null;
        if (jobDataStr) {
          try { jobData = JSON.parse(jobDataStr); } catch (e) {}
          localStorage.removeItem('draft_source_job_data');
        }
        
        // localStorage'ı temizle
        localStorage.removeItem('draft_source_job_id');
        localStorage.removeItem('draft_source_job_name');
        
        // Clear URL parameter
        window.history.replaceState({}, document.title, '/purchasing/purchased');
        
        const now = new Date();
        const currentDate = now.toISOString().split('T')[0];
        const currentTime = now.toTimeString().slice(0, 5);

        const reqToken = localStorage.getItem('fortis_token');
        const reqHeaders = { 'Authorization': `Bearer ${reqToken}`, 'Content-Type': 'application/json' };

        const buildItemsFromOffer = (offer) => (offer.items || []).map(item => ({
          stok_karti_id: item.stok_karti_id || '',
          product_name: item.product_name || '',
          model: item.model || '',
          quantity: item.quantity?.toString() || '',
          unit: item.unit || 'adet',
          purchase_price: item.price?.toString() || '',
          kdv_orani: item.kdv_orani ?? 20
        }));
        const buildStokInputs = (offer) => (offer.items || []).map(item => {
          const name = item.product_name || '';
          const model = item.model || '';
          return model ? `${name} - ${model}` : name;
        });
        const buildPaymentPlan = (offer) => {
          const plan = [];
          if (offer.installments?.length) {
            offer.installments.forEach(inst => plan.push({ type: 'nakit', date: inst.date || '', amount: inst.amount?.toString() || '', description: 'Taksit' }));
          }
          if (offer.checks?.length) {
            offer.checks.forEach(chk => plan.push({ type: 'cek', date: chk.date || '', amount: chk.amount?.toString() || '', description: chk.bank || 'Çek' }));
          }
          return plan;
        };

        (async () => {
          // Seçilen tekliften ürün kalemlerini ve cari hesap bilgilerini doldur
          let prefillItems = [{ stok_karti_id: '', product_name: '', model: '', quantity: '', unit: 'adet', purchase_price: '', kdv_orani: 20 }];
          let prefillCariId = '';
          let prefillCariName = '';
          let prefillDescription = finalJobName ? `İş: ${decodeURIComponent(finalJobName)}` : '';
          let prefillBranchId = jobData?.branch_id || '';
          let prefillStokInputs = [''];
          let prefillPaymentPlan = [];

          if (jobData) {
            const activeTeklif = jobData.selected_teklif || (jobData.teklifler?.length > 0 ? jobData.teklifler[0] : null);
            const offerId = jobData.selected_offer_id || activeTeklif?.offer_id || null;

            // 1) original_offer varsa direkt kullan, 2) yoksa offer_id ile API'den çek
            let offer = activeTeklif?.original_offer || null;
            if ((!offer || !(offer.items?.length)) && offerId) {
              try {
                const r = await fetch(`${API_URL}/api/purchasing/offers/${offerId}`, { headers: reqHeaders });
                if (r.ok) offer = await r.json();
              } catch (e) { console.error('Teklif verisi çekilemedi:', e); }
            }

            if (offer && offer.items?.length) {
              prefillItems = buildItemsFromOffer(offer);
              prefillStokInputs = buildStokInputs(offer);
              prefillPaymentPlan = buildPaymentPlan(offer);
            }

            // Cari hesap (firma) bilgisi
            prefillCariName = offer?.cari_hesap_name || activeTeklif?.firma_adi || prefillCariName;
            prefillCariId = offer?.cari_hesap_id || activeTeklif?.original_offer?.cari_hesap_id || '';

            // Açıklama
            if (activeTeklif?.aciklama) {
              prefillDescription = `İş: ${decodeURIComponent(finalJobName || '')}\n${activeTeklif.aciklama}`;
            }

            // Ödeme tipi nakit ise tek seferde toplam (ödeme planı bulunamadıysa)
            if (prefillPaymentPlan.length === 0 && activeTeklif?.payment_type === 'nakit' && activeTeklif?.toplam_fiyat) {
              const toplamStr = activeTeklif.toplam_fiyat.toString().replace(/[^\d.,]/g, '').replace('.', '').replace(',', '.');
              prefillPaymentPlan.push({ type: 'nakit', date: currentDate, amount: toplamStr || '', description: 'Nakit Ödeme' });
            }
          }

          setFormData({
            target_branch_id: prefillBranchId,
            cari_hesap_id: prefillCariId,
            entry_date: currentDate,
            entry_time: currentTime,
            estimated_arrival_date: '',
            estimated_arrival_time: '',
            items: prefillItems,
            documents: [],
            description: prefillDescription,
            payment_plan: prefillPaymentPlan,
            related_job_id: finalJobId
          });
          setSelectedCariName(prefillCariName);
          setCariSearchInput(prefillCariName);
          setStokSearchInputs(prefillStokInputs);
          setShowStokDropdowns(prefillStokInputs.map(() => false));
          setShowAddModal(true);
        })();
      } else {
        // No jobId, check localStorage for pending data
        const pendingJobStr = localStorage.getItem('pending_order_from_job');
        if (pendingJobStr) {
          try {
            const jobData = JSON.parse(pendingJobStr);
            setPendingJobData(jobData);
            localStorage.removeItem('pending_order_from_job');
            navigate('/purchasing/purchased', { replace: true });
          } catch (error) {
            console.error('Pending job data parse error:', error);
            localStorage.removeItem('pending_order_from_job');
            navigate('/purchasing/purchased', { replace: true });
          }
        } else {
          // No pending data, just clear URL and open empty modal
          navigate('/purchasing/purchased', { replace: true });
          // Open empty modal
          const now = new Date();
          const currentDate = now.toISOString().split('T')[0];
          const currentTime = now.toTimeString().slice(0, 5);
          
          setFormData({
            target_branch_id: '',
            cari_hesap_id: '',
            entry_date: currentDate,
            entry_time: currentTime,
            estimated_arrival_date: '',
            estimated_arrival_time: '',
            items: [{ stok_karti_id: '', product_name: '', quantity: '', unit: 'adet', purchase_price: '' }],
            documents: [],
            description: '',
            related_job_id: '' // Boş
          });
          setSelectedCariName('');
          setCariSearchInput('');
          setStokSearchInputs(['']);
          setShowStokDropdowns([false]);
          setShowAddModal(true);
        }
      }
    }
  }, []); // Sadece component mount olduğunda çalış

  // Open modal with pre-filled data when pendingJobData is set and branches/cariHesaplar are loaded
  useEffect(() => {
    if (pendingJobData && branches.length > 0 && cariHesaplar.length > 0) {
      // Get current date and time
      const now = new Date();
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().slice(0, 5);
      
      // Find branch id
      let branchId = pendingJobData.sube_id || '';
      if (!branchId && pendingJobData.branch_name) {
        const foundBranch = branches.find(b => b.name === pendingJobData.branch_name);
        if (foundBranch) branchId = foundBranch.id;
      }
      
      // Save job_id for completing the job after order is created
      if (pendingJobData.job_id) {
        setSourceJobId(pendingJobData.job_id);
      }
      
      setFormData({
        target_branch_id: branchId,
        cari_hesap_id: '',
        entry_date: currentDate,
        entry_time: currentTime,
        estimated_arrival_date: '',
        estimated_arrival_time: '',
        items: [{ stok_karti_id: '', product_name: '', quantity: '', unit: 'adet', purchase_price: '' }],
        documents: [],
        description: `İş: ${pendingJobData.konu || ''}`
      });
      
      // Cari hesap alanı boş olacak
      setSelectedCariName('');
      setCariSearchInput('');
      setStokSearchInputs(['']);
      setFilteredStokList([stockCards]);
      setShowStokDropdowns([false]);
      setShowAddModal(true);
      
      // Clear pending job data
      setPendingJobData(null);
    }
  }, [pendingJobData, branches, cariHesaplar, stockCards]);

  // Cari Hesap search filter
  useEffect(() => {
    if (cariSearchInput.trim()) {
      const filtered = cariHesaplar.filter(c => 
        c.name?.toLowerCase().includes(cariSearchInput.toLowerCase())
      );
      setFilteredCari(filtered);
    } else {
      setFilteredCari(cariHesaplar);
    }
  }, [cariSearchInput, cariHesaplar]);

  // Stok Kartı search filter
  const handleStokSearch = (index, value) => {
    const newInputs = [...stokSearchInputs];
    newInputs[index] = value;
    setStokSearchInputs(newInputs);

    const newFiltered = [...filteredStokList];
    if (value.trim()) {
      newFiltered[index] = stockCards.filter(s =>
        s.product_name?.toLowerCase().includes(value.toLowerCase()) ||
        s.model?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      newFiltered[index] = stockCards;
    }
    setFilteredStokList(newFiltered);
  };

  // Select Cari Hesap
  const handleSelectCari = (cari) => {
    setFormData(prev => ({ ...prev, cari_hesap_id: cari.id }));
    setSelectedCariName(cari.name);
    setCariSearchInput(cari.name);
    setShowCariDropdown(false);
  };

  // Select Stok Kartı
  const handleSelectStok = (index, stok) => {
    const displayName = stok.model ? `${stok.product_name} - ${stok.model}` : stok.product_name;
    const newItems = [...formData.items];
    newItems[index] = { 
      ...newItems[index], 
      stok_karti_id: stok.id, 
      product_name: stok.product_name,
      model: stok.model || '',
      unit: stok.unit || 'adet' 
    };
    setFormData(prev => ({ ...prev, items: newItems }));
    
    const newInputs = [...stokSearchInputs];
    newInputs[index] = displayName;
    setStokSearchInputs(newInputs);
    
    const newDropdowns = [...showStokDropdowns];
    newDropdowns[index] = false;
    setShowStokDropdowns(newDropdowns);
  };

  // Add new item row
  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { stok_karti_id: '', product_name: '', model: '', quantity: '', unit: 'adet', purchase_price: '', kdv_orani: 20 }]
    }));
    setStokSearchInputs(prev => [...prev, '']);
    setFilteredStokList(prev => [...prev, stockCards]);
    setShowStokDropdowns(prev => [...prev, false]);
  };

  // Remove item row
  const handleRemoveItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
    setStokSearchInputs(prev => prev.filter((_, i) => i !== index));
    setFilteredStokList(prev => prev.filter((_, i) => i !== index));
    setShowStokDropdowns(prev => prev.filter((_, i) => i !== index));
  };

  // Update item quantity
  const handleItemQuantityChange = (index, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], quantity: value };
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  // Update item purchase price
  const handleItemPriceChange = (index, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], purchase_price: value };
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          documents: [...prev.documents, { name: file.name, data: reader.result, type: file.type }]
        }));
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // Remove file
  const handleRemoveFile = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Open Add Modal
  const openAddModal = () => {
    // Get current date and time
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().slice(0, 5);
    
    setFormData({
      target_branch_id: '',
      cari_hesap_id: '',
      entry_date: currentDate,
      entry_time: currentTime,
      estimated_arrival_date: '',
      estimated_arrival_time: '',
      currency: 'TL',
      items: [{ stok_karti_id: '', product_name: '', model: '', quantity: '', unit: 'adet', purchase_price: '', kdv_orani: 20 }],
      documents: [],
      description: '',
      nakliye_var: false,
      nakliye_ucreti: '',
      payment_plan: []
    });
    setSelectedCariName('');
    setCariSearchInput('');
    setStokSearchInputs(['']);
    setFilteredStokList([stockCards]);
    setShowStokDropdowns([false]);
    setShowAddModal(true);
  };

  // Fetch cari hesaplar for demirbaş modal
  const fetchDemirbasCari = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/accounting/cari-hesaplar`, { headers });
      if (response.ok) {
        const data = await response.json();
        setDemirbasCariList(data);
      }
    } catch (error) {
      console.error('Demirbaş cari hesaplar yüklenemedi:', error);
    }
  }, []);

  // Open Demirbaş Modal
  const openDemirbasModal = () => {
    fetchDemirbasCari();
    setDemirbasFormData({
      product_name: '',
      quantity: 1,
      unit: 'adet',
      purchase_price: 0,
      description: '',
      serial_number: '',
      supplier_id: '',
      supplier: '',
      brand: '',
      status: 'active',
      documents: [],
      invoice_documents: []
    });
    setShowDemirbasModal(true);
  };

  // Handle demirbaş file upload
  const handleDemirbasFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newDocuments = [];
    
    for (const file of files) {
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newDocuments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          data: base64,
          uploaded_at: new Date().toISOString()
        });
      } catch (error) {
        console.error('Dosya yükleme hatası:', error);
      }
    }

    setDemirbasFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...newDocuments]
    }));
    e.target.value = '';
  };

  // Handle demirbaş invoice file upload
  const handleDemirbasInvoiceUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newDocuments = [];
    
    for (const file of files) {
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newDocuments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          data: base64,
          uploaded_at: new Date().toISOString()
        });
      } catch (error) {
        console.error('Dosya yükleme hatası:', error);
      }
    }

    setDemirbasFormData(prev => ({
      ...prev,
      invoice_documents: [...prev.invoice_documents, ...newDocuments]
    }));
    e.target.value = '';
  };

  // Submit demirbaş
  const handleDemirbasSubmit = async () => {
    if (!demirbasFormData.product_name.trim()) {
      toast.error('Ürün adı gereklidir');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/purchasing/demirbaslar`, {
        method: 'POST',
        headers,
        body: JSON.stringify(demirbasFormData)
      });

      if (response.ok) {
        toast.success('Demirbaş eklendi');
        setShowDemirbasModal(false);
        navigate('/purchasing/demirbaslar');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Demirbaş eklenemedi');
      }
    } catch (error) {
      console.error('Demirbaş ekle hatası:', error);
      toast.error('Demirbaş eklenirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter data by search term
  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.cari_hesap_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.branch_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Add new order
  const handleAddOrder = async () => {
    // Şube seçimi kontrolü
    if (!formData.target_branch_id) {
      toast.error('Lütfen bir şube seçiniz');
      return;
    }
    
    if (!formData.cari_hesap_id) {
      toast.error('Cari hesap seçiniz');
      return;
    }
    
    // Tahmini geliş tarihi kontrolü
    if (!formData.estimated_arrival_date) {
      toast.error('Tahmini depoya geliş tarihi giriniz');
      return;
    }
    
    const validItems = formData.items.filter(item => (item.stok_karti_id || item.product_name) && item.quantity);
    if (validItems.length === 0) {
      toast.error('En az bir ürün ekleyiniz');
      return;
    }

    // Belge zorunlu kontrolü
    if (formData.documents.length === 0) {
      toast.error('En az bir belge yüklemeniz gerekmektedir');
      return;
    }

    setSubmitting(true);
    try {
      // Get target branch info
      const targetBranch = branches.find(b => b.id === formData.target_branch_id);
      
      // Map items with product names and purchase price
      const itemsWithNames = validItems.map(item => {
        const stokKarti = stockCards.find(s => s.id === item.stok_karti_id);
        return {
          ...item,
          product_name: item.product_name || stokKarti?.product_name || '',
          purchase_price: item.purchase_price ? parseFloat(item.purchase_price) : null
        };
      });

      // Sipariş verilerini hazırla
      const orderPayload = {
        branch_id: formData.target_branch_id,
        branch_name: targetBranch?.name || '',
        cari_hesap_id: formData.cari_hesap_id,
        cari_hesap_name: selectedCariName,
        currency: formData.currency || 'TL',
        items: itemsWithNames,
        documents: formData.documents,
        description: formData.description || '',
        nakliye_var: formData.nakliye_var || false,
        nakliye_ucreti: formData.nakliye_ucreti || '',
        payment_plan: formData.payment_plan || [],
        entry_datetime: `${formData.entry_date}T${formData.entry_time}:00`,
        estimated_arrival_datetime: formData.estimated_arrival_time 
          ? `${formData.estimated_arrival_date}T${formData.estimated_arrival_time}:00`
          : `${formData.estimated_arrival_date}T12:00:00`
      };

      // Eğer bir işten gelindiyse (jobId varsa), taslak olarak kaydet
      const jobId = sourceJobId || sourceJobIdRef.current || formData.related_job_id;
      
      if (jobId) {
        const draftResponse = await fetch(`${API_URL}/api/purchasing/jobs/${jobId}/save-draft`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            draft_type: 'order',
            draft_data: orderPayload
          })
        });

        if (draftResponse.ok) {
          toast.success('Sipariş taslağı kaydedildi, yönetim onayı bekleniyor');
          setSourceJobId(null);
          navigate('/purchasing/jobs?tab=completed');
        } else {
          const err = await draftResponse.json();
          toast.error(err.detail || 'Taslak kaydetme başarısız');
        }
      } else {
        // Normal sipariş akışı (iş'den gelmiyorsa direkt kaydet)
        const payload = {
          ...formData,
          items: itemsWithNames,
          branch_id: formData.target_branch_id,
          branch_name: targetBranch?.name || '',
          cari_hesap_name: selectedCariName,
          entry_datetime: orderPayload.entry_datetime,
          estimated_arrival_datetime: orderPayload.estimated_arrival_datetime,
          add_to_gelecek_urunler: true,
          status: 'ordered',
          source_job_id: sourceJobId || sourceJobIdRef.current || formData.related_job_id || null
        };

        const response = await fetch(`${API_URL}/api/purchasing/orders`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          toast.success('Sipariş eklendi ve şube deposuna bildirildi');
          setShowAddModal(false);
          fetchOrders();
        } else {
          const err = await response.json();
          toast.error(err.detail || 'Sipariş eklenemedi');
        }
      }
    } catch (error) {
      console.error('Sipariş ekle hatası:', error);
      toast.error(error.message || 'Sipariş eklenirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  // Mark as arrived
  const handleMarkAsArrived = async (order) => {
    try {
      const response = await fetch(`${API_URL}/api/purchasing/orders/${order.id}/arrived`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        toast.success('Ürün geldi olarak işaretlendi');
        fetchOrders();
        fetchArrivedProducts();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  // Mark as completed
  const handleMarkAsCompleted = async (order) => {
    try {
      const response = await fetch(`${API_URL}/api/purchasing/orders/${order.id}/complete`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        toast.success('Sipariş tamamlandı');
        fetchArrivedProducts();
        fetchCompletedOrders();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  // Delete order
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Bu siparişi silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`${API_URL}/api/purchasing/orders/${orderId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        toast.success('Sipariş silindi');
        fetchOrders();
        fetchArrivedProducts();
        fetchCompletedOrders();
      } else {
        toast.error('Silme işlemi başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Excel Export Fonksiyonları
  const exportToExcel = (data, headers, filename) => {
    const csvContent = [headers, ...data]
      .map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} indirildi`);
  };

  // Sipariş Edilen Excel Export
  const exportOrderedToExcel = () => {
    const filteredData = orders.filter(order => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return order.products?.some(p => 
        p.product_name?.toLowerCase().includes(term) ||
        p.stock_card_name?.toLowerCase().includes(term)
      ) || order.supplier?.toLowerCase().includes(term);
    });
    
    const headers = ['Sipariş No', 'Tedarikçi', 'Ürün Adı', 'Miktar', 'Birim', 'Birim Fiyat', 'Toplam', 'Şube', 'Sipariş Tarihi', 'Durum'];
    const rows = [];
    
    filteredData.forEach(order => {
      order.products?.forEach(product => {
        rows.push([
          order.id?.substring(0, 8) || '',
          order.supplier || order.cari_hesap_name || '',
          product.product_name || product.stock_card_name || '',
          product.quantity || 1,
          product.unit || 'adet',
          product.unit_price || 0,
          product.total_price || 0,
          order.branch_name || '',
          formatDate(order.created_at),
          order.status === 'ordered' ? 'Sipariş Edildi' : order.status === 'arrived' ? 'Geldi' : order.status
        ]);
      });
    });
    
    exportToExcel(rows, headers, 'siparis_edilen');
  };

  // Gelen Ürünler Excel Export
  const exportArrivedToExcel = () => {
    const filteredData = arrivedProducts.filter(item => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return item.product_name?.toLowerCase().includes(term) ||
             item.supplier?.toLowerCase().includes(term) ||
             item.branch_name?.toLowerCase().includes(term);
    });
    
    const headers = ['Ürün Adı', 'Miktar', 'Birim', 'Tedarikçi', 'Şube', 'Geliş Tarihi'];
    const rows = filteredData.map(item => [
      item.product_name || '',
      item.quantity || 1,
      item.unit || 'adet',
      item.supplier || item.cari_hesap_name || '',
      item.branch_name || '',
      formatDate(item.arrival_date || item.created_at)
    ]);
    
    exportToExcel(rows, headers, 'gelen_urunler');
  };

  // Eksik/Hatalı Excel Export
  const exportDefectiveToExcel = () => {
    const filteredData = defectiveProducts.filter(item => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return item.products?.some(p => 
        p.product_name?.toLowerCase().includes(term)
      ) || item.supplier?.toLowerCase().includes(term);
    });
    
    const headers = ['Sipariş No', 'Tedarikçi', 'Ürün Adı', 'Sorun Tipi', 'Sorun Miktarı', 'Açıklama', 'Durum', 'Bildirim Tarihi'];
    const rows = [];
    
    filteredData.forEach(order => {
      order.products?.forEach(product => {
        rows.push([
          order.id?.substring(0, 8) || '',
          order.supplier || order.cari_hesap_name || '',
          product.product_name || '',
          product.issue_type === 'eksik' ? 'Eksik' : product.issue_type === 'hatali' ? 'Hatalı' : product.issue_type === 'hasarli' ? 'Hasarlı' : product.issue_type,
          product.issue_quantity || 0,
          product.description || '',
          order.defective_status === 'acik' ? 'Açık' : order.defective_status === 'cozuldu' ? 'Çözüldü' : order.defective_status === 'gideriliyor' ? 'Sipariş Verildi' : order.defective_status,
          formatDate(order.defective_report_date || order.created_at)
        ]);
      });
    });
    
    exportToExcel(rows, headers, 'eksik_hatali');
  };

  // İptal Edilenler Excel Export
  const exportCancelledToExcel = () => {
    const filteredData = cancelledDefective.filter(item => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return item.products?.some(p => 
        p.product_name?.toLowerCase().includes(term)
      ) || item.supplier?.toLowerCase().includes(term);
    });
    
    const headers = ['Sipariş No', 'Tedarikçi', 'Ürün Adı', 'Sorun Tipi', 'Sorun Miktarı', 'Açıklama', 'İptal Tarihi'];
    const rows = [];
    
    filteredData.forEach(order => {
      order.products?.forEach(product => {
        rows.push([
          order.id?.substring(0, 8) || '',
          order.supplier || order.cari_hesap_name || '',
          product.product_name || '',
          product.issue_type === 'eksik' ? 'Eksik' : product.issue_type === 'hatali' ? 'Hatalı' : product.issue_type === 'hasarli' ? 'Hasarlı' : product.issue_type,
          product.issue_quantity || 0,
          product.description || '',
          formatDate(order.defective_cancelled_date || order.updated_at)
        ]);
      });
    });
    
    exportToExcel(rows, headers, 'iptal_edilenler');
  };

  // Tamamlanan Excel Export
  const exportCompletedToExcel = () => {
    const filteredData = completedOrders.filter(order => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return order.products?.some(p => 
        p.product_name?.toLowerCase().includes(term) ||
        p.stock_card_name?.toLowerCase().includes(term)
      ) || order.supplier?.toLowerCase().includes(term);
    });
    
    const headers = ['Sipariş No', 'Tedarikçi', 'Ürün Adı', 'Miktar', 'Birim', 'Birim Fiyat', 'Toplam', 'Şube', 'Tamamlanma Tarihi'];
    const rows = [];
    
    filteredData.forEach(order => {
      order.products?.forEach(product => {
        rows.push([
          order.id?.substring(0, 8) || '',
          order.supplier || order.cari_hesap_name || '',
          product.product_name || product.stock_card_name || '',
          product.quantity || 1,
          product.unit || 'adet',
          product.unit_price || 0,
          product.total_price || 0,
          order.branch_name || '',
          formatDate(order.completed_at || order.updated_at)
        ]);
      });
    });
    
    exportToExcel(rows, headers, 'tamamlanan');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ordered':
        return <Badge className="bg-blue-100 text-blue-800">Sipariş Edildi</Badge>;
      case 'arrived':
        return <Badge className="bg-yellow-100 text-yellow-800">Geldi</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Tamamlandı</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getIssueTypeBadge = (issueType) => {
    switch (issueType) {
      case 'eksik':
        return <Badge className="bg-orange-100 text-orange-800">Eksik</Badge>;
      case 'hatali':
        return <Badge className="bg-red-100 text-red-800">Hatalı</Badge>;
      case 'hasarli':
        return <Badge className="bg-purple-100 text-purple-800">Hasarlı</Badge>;
      default:
        return <Badge variant="outline">{issueType}</Badge>;
    }
  };

  const getDefectiveStatusBadge = (status) => {
    switch (status) {
      case 'acik':
        return <Badge className="bg-red-100 text-red-800">Açık</Badge>;
      case 'cozuldu':
        return <Badge className="bg-green-100 text-green-800">Çözüldü</Badge>;
      case 'iptal':
        return <Badge className="bg-gray-100 text-gray-800">İptal Edildi</Badge>;
      case 'gideriliyor':
        return <Badge className="bg-blue-100 text-blue-800">Sipariş Verildi</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Toggle defective order expansion
  const toggleDefectiveExpansion = (orderId) => {
    setExpandedDefective(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  // Toggle cancelled order expansion
  const toggleCancelledExpansion = (orderId) => {
    setExpandedCancelled(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  // Handle cancel defective item/group
  const handleCancelDefective = async () => {
    if (!cancellingItem) return;
    
    try {
      // Cancel all items in the group
      const itemIds = cancellingItem.items ? cancellingItem.items.map(item => item.id) : [cancellingItem.id];
      
      for (const itemId of itemIds) {
        const response = await fetch(`${API_URL}/api/purchasing/hatali-urunler/${itemId}/iptal`, {
          method: 'PUT',
          headers: {
            ...headers,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ cancel_reason: cancelReason })
        });
        
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'İptal işlemi başarısız');
        }
      }
      
      toast.success('Kayıt iptal edildi');
      setShowCancelModal(false);
      setShowDetailModal(false);
      setCancelReason('');
      setCancellingItem(null);
      fetchDefectiveProducts();
      fetchCancelledDefective();
    } catch (error) {
      console.error('İptal hatası:', error);
      toast.error(error.message || 'İptal işlemi sırasında bir hata oluştu');
    }
  };

  // Open cancel modal
  const openCancelModal = (item) => {
    setCancellingItem(item);
    setCancelReason('');
    setShowCancelModal(true);
  };

  // Open complete order modal (for re-ordering defective items)
  const openCompleteOrderModal = (item) => {
    setCompleteOrderItem(item);
    
    // Initialize quantities as empty (user will fill in)
    const quantities = {};
    if (item.items) {
      item.items.forEach(i => {
        quantities[i.id] = ''; // Start empty
      });
    } else {
      quantities[item.id] = ''; // Start empty
    }
    setCompleteOrderQuantities(quantities);
    
    // Auto-fill cari hesap from defective item
    const cariName = item.cari_hesap || (item.items && item.items[0]?.cari_hesap) || '';
    // Find cari hesap id from name
    const foundCari = cariHesaplar.find(c => c.name === cariName);
    setCompleteOrderCariId(foundCari?.id || '');
    
    setCompleteOrderDate('');
    setShowDetailModal(false);
    setShowCompleteOrderModal(true);
  };

  // Handle complete order submission
  const handleCompleteOrder = async () => {
    if (!completeOrderItem) return;
    
    try {
      setSubmitting(true);
      
      // Prepare items for the order
      const items = completeOrderItem.items ? completeOrderItem.items.map(i => ({
        stok_karti_id: i.stok_karti_id || '',
        product_name: i.product_name || '',
        quantity: completeOrderQuantities[i.id] || 0,
        unit: i.unit || 'adet',
        purchase_price: 0
      })).filter(i => i.quantity > 0) : [{
        stok_karti_id: completeOrderItem.stok_karti_id || '',
        product_name: completeOrderItem.product_name || '',
        quantity: completeOrderQuantities[completeOrderItem.id] || 0,
        unit: completeOrderItem.unit || 'adet',
        purchase_price: 0
      }].filter(i => i.quantity > 0);
      
      if (items.length === 0) {
        toast.error('En az bir ürün için miktar giriniz');
        setSubmitting(false);
        return;
      }
      
      const branchId = completeOrderItem.branch_id || (completeOrderItem.items && completeOrderItem.items[0]?.branch_id) || '';
      
      // Find cari hesap name
      const selectedCari = cariHesaplar.find(c => c.id === completeOrderCariId);
      
      // Get parent order ID from the defective item's source_id (original order)
      const parentOrderId = completeOrderItem.source_id || completeOrderItem.id;
      
      // Create order for gelecek ürünler
      const orderData = {
        target_branch_id: branchId,
        cari_hesap_id: completeOrderCariId || null,
        cari_hesap_name: selectedCari?.name || '',
        estimated_arrival_datetime: completeOrderDate || null,
        description: `Eksik/Hatalı ürün tamamlama siparişi`,
        items: items,
        documents: [],
        source_type: 'defective_completion',
        source_id: completeOrderItem.id,
        parent_order_id: parentOrderId,  // Link to original order for delivery history tracking
        add_to_gelecek_urunler: true  // This flag sends it to depot's "Beklenen Teslimatlar"
      };
      
      const response = await fetch(`${API_URL}/api/purchasing/orders`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Sipariş oluşturulamadı');
      }
      
      // Mark defective items as "cozuldu" (resolved) - tracking continues via new order in "Beklenen Teslimatlar"
      const itemIds = completeOrderItem.items ? completeOrderItem.items.map(item => item.id) : [completeOrderItem.id];
      for (const itemId of itemIds) {
        try {
          await fetch(`${API_URL}/api/purchasing/hatali-urunler/${itemId}/cozuldu`, {
            method: 'PUT',
            headers: {
              ...headers,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              resolution_notes: 'Yeni sipariş oluşturuldu. Takip "Beklenen Teslimatlar" sekmesinden yapılabilir.'
            })
          });
        } catch (e) {
          console.log('Status update failed for item:', itemId);
        }
      }
      
      toast.success('Sipariş oluşturuldu! Depo panelinde "Beklenen Teslimatlar" sekmesine eklendi. Eksik/Hatalı kaydı çözüldü olarak işaretlendi.');
      setShowCompleteOrderModal(false);
      setCompleteOrderItem(null);
      setCompleteOrderQuantities({});
      fetchOrders();
      fetchDefectiveProducts();
    } catch (error) {
      console.error('Sipariş hatası:', error);
      toast.error(error.message || 'Sipariş oluşturulurken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort data
  const sortData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'order_date':
          aVal = new Date(a.order_date || a.entry_datetime || a.created_at || 0);
          bVal = new Date(b.order_date || b.entry_datetime || b.created_at || 0);
          break;
        case 'created_at':
          aVal = new Date(a.created_at || a.hata_cikma_zamani || 0);
          bVal = new Date(b.created_at || b.hata_cikma_zamani || 0);
          break;
        case 'cancelled_at':
          aVal = new Date(a.cancelled_at || a.created_at || 0);
          bVal = new Date(b.cancelled_at || b.created_at || 0);
          break;
        case 'branch_name':
          aVal = (a.branch_name || '').toLowerCase();
          bVal = (b.branch_name || '').toLowerCase();
          break;
        case 'item_count':
          aVal = a.item_count || a.items?.length || 1;
          bVal = b.item_count || b.items?.length || 1;
          break;
        case 'cari_hesap_name':
          aVal = (a.cari_hesap_name || '').toLowerCase();
          bVal = (b.cari_hesap_name || '').toLowerCase();
          break;
        case 'cari_hesap':
          aVal = (a.cari_hesap || a.cari_hesap_name || '').toLowerCase();
          bVal = (b.cari_hesap || b.cari_hesap_name || '').toLowerCase();
          break;
        case 'expected_date':
          aVal = new Date(a.expected_date || a.estimated_arrival_datetime || 0);
          bVal = new Date(b.expected_date || b.estimated_arrival_datetime || 0);
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'cancel_reason':
          aVal = (a.cancel_reason || a.items?.[0]?.cancel_reason || '').toLowerCase();
          bVal = (b.cancel_reason || b.items?.[0]?.cancel_reason || '').toLowerCase();
          break;
        default:
          aVal = a[sortConfig.key] || '';
          bVal = b[sortConfig.key] || '';
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronDown className="h-3 w-3 text-gray-300 ml-1" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronDown className="h-3 w-3 text-blue-600 ml-1 rotate-180" />
      : <ChevronDown className="h-3 w-3 text-blue-600 ml-1" />;
  };

  // Filter defective data (for grouped structure)
  const filterDefectiveData = (data) => {
    if (!searchTerm) return data;
    return data.filter(item => {
      // Search in main fields
      const mainMatch = 
        item.branch_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.cari_hesap?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Search in items
      const itemMatch = item.items?.some(i => 
        i.product_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      return mainMatch || itemMatch;
    });
  };

  // Render table for defective products (grouped by order, expandable)
  const renderDefectiveTable = (data) => {
    const filteredData = sortData(filterDefectiveData(data));
    
    return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">
            <Checkbox 
              checked={filteredData.length > 0 && filteredData.every(o => selectedItems.includes(o.id))}
              onCheckedChange={() => toggleSelectAll(filteredData)}
            />
          </TableHead>
          <TableHead className="w-10"></TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('created_at')}
          >
            <div className="flex items-center">
              Kayıt Tarihi
              <SortIcon columnKey="created_at" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('branch_name')}
          >
            <div className="flex items-center">
              Şube
              <SortIcon columnKey="branch_name" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('item_count')}
          >
            <div className="flex items-center">
              Ürün Sayısı
              <SortIcon columnKey="item_count" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('cari_hesap')}
          >
            <div className="flex items-center">
              Tedarikçi
              <SortIcon columnKey="cari_hesap" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('status')}
          >
            <div className="flex items-center">
              Durum
              <SortIcon columnKey="status" />
            </div>
          </TableHead>
          <TableHead className="text-right">İşlemler</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filteredData.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-8 text-gray-500">
              {searchTerm ? 'Arama sonucu bulunamadı' : 'Eksik veya hatalı ürün kaydı yok'}
            </TableCell>
          </TableRow>
        ) : (
          filteredData.map((order) => (
            <React.Fragment key={order.id}>
              {/* Main Order Row */}
              <TableRow 
                data-testid={`defective-order-row-${order.id}`}
                className={`cursor-pointer hover:bg-red-50 ${expandedDefective[order.id] ? 'bg-red-50' : ''} ${selectedItems.includes(order.id) ? 'bg-red-100' : ''}`}
              >
                <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                  <Checkbox 
                    checked={selectedItems.includes(order.id)}
                    onCheckedChange={() => toggleItemSelection(order.id)}
                  />
                </TableCell>
                <TableCell className="w-10" onClick={() => toggleDefectiveExpansion(order.id)}>
                  {order.items && order.items.length > 0 ? (
                    expandedDefective[order.id] ? (
                      <ChevronDown className="h-4 w-4 text-red-600" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-300" />
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {formatDateTime(order.created_at || order.hata_cikma_zamani)}
                  </div>
                </TableCell>
                <TableCell>{order.branch_name || '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="gap-1 border-red-300 text-red-700">
                    <Layers className="h-3 w-3" />
                    {order.item_count || order.items?.length || 1} Kalem
                  </Badge>
                </TableCell>
                <TableCell>{order.cari_hesap || '-'}</TableCell>
                <TableCell>
                  {order.has_open_issues ? (
                    <Badge className="bg-red-100 text-red-800">Açık Sorun Var</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800">Tümü Çözüldü</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedItem({...order, _type: 'defective_group'}); setShowDetailModal(true); }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
              
              {/* Expanded Items Row */}
              {expandedDefective[order.id] && order.items && order.items.length > 0 && (
                <TableRow className="bg-red-50/50">
                  <TableCell colSpan={7} className="p-0">
                    <div className="p-4 border-l-4 border-red-400">
                      <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        EKSİK/HATALI ÜRÜNLER ({order.items.length})
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-red-100/50">
                            <TableHead className="text-xs">Ürün Adı</TableHead>
                            <TableHead className="text-xs">Sorun Tipi</TableHead>
                            <TableHead className="text-xs">Beklenen</TableHead>
                            <TableHead className="text-xs">Gelen</TableHead>
                            <TableHead className="text-xs">Eksik</TableHead>
                            <TableHead className="text-xs">Teslim Tarihi</TableHead>
                            <TableHead className="text-xs">Durum</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {order.items.map((item, idx) => (
                            <TableRow key={item.id || idx} className="hover:bg-red-100/30">
                              <TableCell className="text-sm font-medium">{item.product_name || '-'}</TableCell>
                              <TableCell className="text-sm">{getIssueTypeBadge(item.issue_type)}</TableCell>
                              <TableCell className="text-sm">{item.expected_quantity || '-'} {item.unit}</TableCell>
                              <TableCell className="text-sm">{item.received_quantity || '-'} {item.unit}</TableCell>
                              <TableCell className="text-sm font-semibold text-red-600">
                                {item.quantity} {item.unit}
                              </TableCell>
                              <TableCell className="text-sm">
                                <div className="flex items-center gap-1 text-emerald-600">
                                  <CheckCircle2 className="h-3 w-3" />
                                  {formatDateTime(item.created_at) || '-'}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm">{getDefectiveStatusBadge(item.status)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))
        )}
      </TableBody>
    </Table>
  )};

  // Render table for cancelled defective items
  const renderCancelledTable = (data) => {
    const filteredData = sortData(filterDefectiveData(data));
    
    return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">
            <Checkbox 
              checked={filteredData.length > 0 && filteredData.every(o => selectedItems.includes(o.id))}
              onCheckedChange={() => toggleSelectAll(filteredData)}
            />
          </TableHead>
          <TableHead className="w-10"></TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('cancelled_at')}
          >
            <div className="flex items-center">
              İptal Tarihi
              <SortIcon columnKey="cancelled_at" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('branch_name')}
          >
            <div className="flex items-center">
              Şube
              <SortIcon columnKey="branch_name" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('item_count')}
          >
            <div className="flex items-center">
              Ürün Sayısı
              <SortIcon columnKey="item_count" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('cari_hesap')}
          >
            <div className="flex items-center">
              Tedarikçi
              <SortIcon columnKey="cari_hesap" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('cancel_reason')}
          >
            <div className="flex items-center">
              İptal Nedeni
              <SortIcon columnKey="cancel_reason" />
            </div>
          </TableHead>
          <TableHead className="text-right">İşlemler</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filteredData.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-8 text-gray-500">
              {searchTerm ? 'Arama sonucu bulunamadı' : 'İptal edilen kayıt yok'}
            </TableCell>
          </TableRow>
        ) : (
          filteredData.map((order) => (
            <React.Fragment key={order.id}>
              {/* Main Order Row */}
              <TableRow 
                data-testid={`cancelled-order-row-${order.id}`}
                className={`cursor-pointer hover:bg-gray-50 ${expandedCancelled[order.id] ? 'bg-gray-50' : ''} ${selectedItems.includes(order.id) ? 'bg-red-50' : ''}`}
              >
                <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                  <Checkbox 
                    checked={selectedItems.includes(order.id)}
                    onCheckedChange={() => toggleItemSelection(order.id)}
                  />
                </TableCell>
                <TableCell className="w-10" onClick={() => toggleCancelledExpansion(order.id)}>
                  {order.items && order.items.length > 0 ? (
                    expandedCancelled[order.id] ? (
                      <ChevronDown className="h-4 w-4 text-gray-600" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )
                  ) : (
                    <X className="h-4 w-4 text-gray-300" />
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {formatDateTime(order.cancelled_at || order.created_at)}
                  </div>
                </TableCell>
                <TableCell>{order.branch_name || '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="gap-1 border-gray-300 text-gray-700">
                    <Layers className="h-3 w-3" />
                    {order.item_count || order.items?.length || 1} Kalem
                  </Badge>
                </TableCell>
                <TableCell>{order.cari_hesap || '-'}</TableCell>
                <TableCell className="max-w-[200px] truncate text-gray-600">
                  {order.cancel_reason || order.items?.[0]?.cancel_reason || '-'}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedItem({...order, _type: 'defective_group', status: 'iptal'}); setShowDetailModal(true); }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
              
              {/* Expanded Items Row */}
              {expandedCancelled[order.id] && order.items && order.items.length > 0 && (
                <TableRow className="bg-gray-50/50">
                  <TableCell colSpan={8} className="p-0">
                    <div className="p-4 border-l-4 border-gray-400">
                      <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-2">
                        <X className="h-4 w-4 text-gray-500" />
                        İPTAL EDİLEN ÜRÜNLER ({order.items.length})
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-100/50">
                            <TableHead className="text-xs">Ürün Adı</TableHead>
                            <TableHead className="text-xs">Sorun Tipi</TableHead>
                            <TableHead className="text-xs">Miktar</TableHead>
                            <TableHead className="text-xs">İptal Nedeni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {order.items.map((item, idx) => (
                            <TableRow key={item.id || idx} className="hover:bg-gray-100/30">
                              <TableCell className="text-sm font-medium">{item.product_name || '-'}</TableCell>
                              <TableCell className="text-sm">{getIssueTypeBadge(item.issue_type)}</TableCell>
                              <TableCell className="text-sm text-gray-600">
                                {item.quantity} {item.unit}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">
                                {item.cancel_reason || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))
        )}
      </TableBody>
    </Table>
  )};

  // Toggle order expansion
  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  // Format datetime for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render table for orders with expandable rows
  const renderOrdersTable = (data, showActions = true, actionType = 'ordered') => {
    const sortedAndFilteredData = sortData(filterData(data));
    
    return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">
            <Checkbox 
              checked={sortedAndFilteredData.length > 0 && sortedAndFilteredData.every(o => selectedItems.includes(o.id))}
              onCheckedChange={() => toggleSelectAll(sortedAndFilteredData)}
            />
          </TableHead>
          <TableHead className="w-10"></TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('order_date')}
          >
            <div className="flex items-center">
              Sipariş Tarihi
              <SortIcon columnKey="order_date" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('branch_name')}
          >
            <div className="flex items-center">
              Şube
              <SortIcon columnKey="branch_name" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('item_count')}
          >
            <div className="flex items-center">
              Ürün Sayısı
              <SortIcon columnKey="item_count" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('cari_hesap_name')}
          >
            <div className="flex items-center">
              Cari Hesap
              <SortIcon columnKey="cari_hesap_name" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('expected_date')}
          >
            <div className="flex items-center">
              Beklenen Tarih
              <SortIcon columnKey="expected_date" />
            </div>
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('status')}
          >
            <div className="flex items-center">
              Durum
              <SortIcon columnKey="status" />
            </div>
          </TableHead>
          <TableHead className="text-right">İşlemler</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedAndFilteredData.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9} className="text-center py-8 text-gray-500">
              {searchTerm ? 'Arama sonucu bulunamadı' : 'Kayıt bulunamadı'}
            </TableCell>
          </TableRow>
        ) : (
          sortedAndFilteredData.map((order) => {
            const orderIsUnread = isUnread(order);
            return (
            <React.Fragment key={order.id}>
              {/* Main Order Row */}
              <TableRow 
                data-testid={`order-row-${order.id}`}
                className={`cursor-pointer hover:bg-gray-50 ${expandedOrders[order.id] ? 'bg-blue-50' : ''} ${orderIsUnread ? 'bg-orange-50/50 border-l-4 border-l-red-500' : ''} ${selectedItems.includes(order.id) ? 'bg-red-50' : ''}`}
                onClick={() => {
                  if (orderIsUnread) markAsRead(order.id);
                  toggleOrderExpansion(order.id);
                }}
              >
                <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                  <Checkbox 
                    checked={selectedItems.includes(order.id)}
                    onCheckedChange={() => toggleItemSelection(order.id)}
                  />
                </TableCell>
                <TableCell className="w-10" onClick={() => {
                  if (orderIsUnread) markAsRead(order.id);
                  toggleOrderExpansion(order.id);
                }}>
                  <div className="flex items-center gap-1">
                    {orderIsUnread && (
                      <Badge className="h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold animate-pulse">
                        YENİ
                      </Badge>
                    )}
                    {order.items && order.items.length > 0 ? (
                      expandedOrders[order.id] ? (
                        <ChevronDown className="h-4 w-4 text-blue-600" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )
                    ) : (
                      <Package className="h-4 w-4 text-gray-300" />
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {formatDateTime(order.order_date || order.entry_datetime || order.created_at)}
                  </div>
                </TableCell>
                <TableCell>{order.branch_name || '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="gap-1">
                    <Layers className="h-3 w-3" />
                    {order.item_count || order.items?.length || 1} Kalem
                  </Badge>
                </TableCell>
                <TableCell>{order.cari_hesap_name || '-'}</TableCell>
                <TableCell>{formatDate(order.expected_date || order.estimated_arrival_datetime)}</TableCell>
                <TableCell>{getStatusBadge(order.status)}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedItem(order); setShowDetailModal(true); }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {showActions && actionType === 'arrived' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-green-600 hover:text-green-700"
                        onClick={() => handleMarkAsCompleted(order)}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              
              {/* Expanded Items Row */}
              {expandedOrders[order.id] && order.items && order.items.length > 0 && (
                <TableRow className="bg-gray-50">
                  <TableCell colSpan={9} className="p-0">
                    <div className="p-4 border-l-4 border-blue-400">
                      <div className="text-xs font-semibold text-gray-500 mb-2">SİPARİŞ KALEMLERİ ({order.items.length})</div>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-100">
                            <TableHead className="text-xs">Ürün Adı</TableHead>
                            <TableHead className="text-xs">Model</TableHead>
                            <TableHead className="text-xs">Miktar</TableHead>
                            <TableHead className="text-xs">Birim</TableHead>
                            <TableHead className="text-xs">Alış Fiyatı</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {order.items.map((item, idx) => (
                            <TableRow key={item.id || idx} className="hover:bg-gray-100">
                              <TableCell className="text-sm font-medium">{item.product_name || '-'}</TableCell>
                              <TableCell className="text-sm text-gray-600">{item.model || '-'}</TableCell>
                              <TableCell className="text-sm">{item.quantity}</TableCell>
                              <TableCell className="text-sm">{item.unit || 'adet'}</TableCell>
                              <TableCell className="text-sm text-emerald-600">
                                {item.purchase_price ? `${item.purchase_price} ₺` : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
          })
        )}
      </TableBody>
    </Table>
  );
  };

  return (
    <div className="space-y-6" data-testid="purchasing-purchased">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alınan Ürünler</h1>
          <p className="text-sm text-gray-500 mt-1">Satın alma siparişlerini yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={openAddModal} 
            className="gap-2 bg-orange-600 hover:bg-orange-700"
            data-testid="add-order-btn"
          >
            <Plus className="h-4 w-4" />
            Yeni Sipariş
          </Button>
          <Button 
            onClick={openDemirbasModal} 
            className="gap-2 bg-purple-600 hover:bg-purple-700"
            data-testid="add-demirbas-btn"
          >
            <ClipboardList className="h-4 w-4" />
            Yeni Demirbaş
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <PackageCheck className="h-5 w-5 text-orange-600" />
              Siparişler
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Ürün veya cari hesap ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                data-testid="search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="ordered" className="gap-2" data-testid="tab-ordered">
                <ShoppingCart className="h-4 w-4" />
                Sipariş Edilen
                {unreadCounts.ordered > 0 ? (
                  <Badge className="ml-1 bg-red-500 text-white text-xs animate-pulse">{unreadCounts.ordered} Yeni</Badge>
                ) : (
                  <Badge className="ml-1 bg-blue-500 text-white text-xs">{orders.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="defective" className="gap-2" data-testid="tab-defective">
                <AlertTriangle className="h-4 w-4" />
                Eksik/Hatalı
                {unreadCounts.defective > 0 ? (
                  <Badge className="ml-1 bg-red-500 text-white text-xs animate-pulse">{unreadCounts.defective} Yeni</Badge>
                ) : (
                  <Badge className="ml-1 bg-red-500 text-white text-xs">{defectiveProducts.length}</Badge>
                )}
              </TabsTrigger>
              {/* İptal Edilenler sekmesi kaldırıldı - P2 fix */}
              <TabsTrigger value="completed" className="gap-2" data-testid="tab-completed">
                <CheckCircle2 className="h-4 w-4" />
                Tamamlanan
                {unreadCounts.completed > 0 ? (
                  <Badge className="ml-1 bg-red-500 text-white text-xs animate-pulse">{unreadCounts.completed} Yeni</Badge>
                ) : (
                  <Badge className="ml-1 bg-green-500 text-white text-xs">{completedOrders.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ordered">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  {selectedItems.length > 0 && (
                    <>
                      <span className="text-sm text-gray-600">{selectedItems.length} kayıt seçildi</span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleteLoading}
                        data-testid="bulk-delete-btn"
                      >
                        {bulkDeleteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        Seçilenleri Sil
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedItems([])}
                      >
                        Seçimi Temizle
                      </Button>
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={exportOrderedToExcel}
                  className="gap-2"
                  disabled={orders.length === 0}
                  data-testid="export-ordered-excel"
                >
                  <Download className="h-4 w-4" />
                  Excel İndir
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Yükleniyor...</div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Henüz sipariş yok</p>
                  <p className="text-sm mt-2">Yeni sipariş eklemek için butona tıklayın</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  {renderOrdersTable(orders, true, 'ordered')}
                </ScrollArea>
              )}
            </TabsContent>

            <TabsContent value="defective">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  {selectedItems.length > 0 && (
                    <>
                      <span className="text-sm text-gray-600">{selectedItems.length} kayıt seçildi</span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleteLoading}
                        data-testid="bulk-delete-btn"
                      >
                        {bulkDeleteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        Seçilenleri Sil
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedItems([])}
                      >
                        Seçimi Temizle
                      </Button>
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={exportDefectiveToExcel}
                  className="gap-2"
                  disabled={defectiveProducts.length === 0}
                  data-testid="export-defective-excel"
                >
                  <Download className="h-4 w-4" />
                  Excel İndir
                </Button>
              </div>
              {defectiveProducts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Eksik veya hatalı ürün kaydı yok</p>
                  <p className="text-sm mt-2">Depo teslim alımlarında eksik/hatalı tespit edildiğinde buraya düşer</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  {renderDefectiveTable(defectiveProducts)}
                </ScrollArea>
              )}
            </TabsContent>

            {/* İptal Edilenler TabsContent kaldırıldı - P2 fix */}

            <TabsContent value="completed">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  {selectedItems.length > 0 && (
                    <>
                      <span className="text-sm text-gray-600">{selectedItems.length} kayıt seçildi</span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={bulkDeleteLoading}
                        data-testid="bulk-delete-btn"
                      >
                        {bulkDeleteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        Seçilenleri Sil
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedItems([])}
                      >
                        Seçimi Temizle
                      </Button>
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={exportCompletedToExcel}
                  className="gap-2"
                  disabled={completedOrders.length === 0}
                  data-testid="export-completed-excel"
                >
                  <Download className="h-4 w-4" />
                  Excel İndir
                </Button>
              </div>
              {completedOrders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Henüz tamamlanan sipariş yok</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  {renderOrdersTable(completedOrders, false, 'completed')}
                </ScrollArea>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add Order Modal - Updated to match Stok Ekle Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-orange-600" />
              Yeni Sipariş Ekle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Şube Seçimi - En üstte */}
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <Label className="flex items-center gap-2 text-orange-700 font-medium">
                <Building className="h-4 w-4" />
                Hedef Şube *
              </Label>
              <Select
                value={formData.target_branch_id}
                onValueChange={(val) => setFormData(prev => ({ ...prev, target_branch_id: val }))}
              >
                <SelectTrigger className="mt-2" data-testid="branch-select">
                  <SelectValue placeholder="Şube seçiniz..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tarih/Saat ve Tahmini Geliş Bilgileri */}
            <div className="grid grid-cols-2 gap-4">
              {/* Giriş Tarihi/Saati */}
              <div className="p-4 bg-gray-50 rounded-lg border">
                <Label className="flex items-center gap-2 text-gray-700 font-medium mb-3">
                  <Calendar className="h-4 w-4" />
                  Sipariş Tarihi/Saati
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-gray-500">Tarih</Label>
                    <Input
                      type="date"
                      value={formData.entry_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, entry_date: e.target.value }))}
                      data-testid="entry-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Saat</Label>
                    <Input
                      type="time"
                      value={formData.entry_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, entry_time: e.target.value }))}
                      data-testid="entry-time"
                    />
                  </div>
                </div>
              </div>

              {/* Tahmini Depoya Geliş */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="flex items-center gap-2 text-blue-700 font-medium mb-3">
                  <Truck className="h-4 w-4" />
                  Tahmini Depoya Geliş *
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-blue-600">Tarih</Label>
                    <Input
                      type="date"
                      value={formData.estimated_arrival_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, estimated_arrival_date: e.target.value }))}
                      className={!formData.estimated_arrival_date ? 'border-red-300' : ''}
                      data-testid="estimated-arrival-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-blue-600">Saat (opsiyonel)</Label>
                    <Input
                      type="time"
                      value={formData.estimated_arrival_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, estimated_arrival_time: e.target.value }))}
                      data-testid="estimated-arrival-time"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Cari Hesap Seçimi */}
            <div className="relative">
              <Label>Cari Hesap (Tedarikçi) *</Label>
              <Input
                value={cariSearchInput}
                onChange={(e) => {
                  setCariSearchInput(e.target.value);
                  setShowCariDropdown(true);
                }}
                onFocus={() => setShowCariDropdown(true)}
                placeholder="Cari hesap ara..."
                data-testid="cari-search-input"
              />
              {showCariDropdown && filteredCari.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredCari.slice(0, 10).map((cari) => (
                    <div
                      key={cari.id}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSelectCari(cari)}
                    >
                      {cari.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Para Birimi */}
            <div>
              <Label>Para Birimi</Label>
              <Select value={formData.currency} onValueChange={(val) => setFormData(prev => ({ ...prev, currency: val }))}>
                <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TL">₺ TL</SelectItem>
                  <SelectItem value="USD">$ USD</SelectItem>
                  <SelectItem value="EUR">€ EUR</SelectItem>
                  <SelectItem value="GBP">£ GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Ürünler */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Ürünler *</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddItem}
                  className="gap-1"
                  data-testid="add-item-btn"
                >
                  <Plus className="h-4 w-4" />
                  Ürün Ekle
                </Button>
              </div>
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 relative">
                      <Label className="text-xs">Stok Kartı</Label>
                      <Input
                        value={stokSearchInputs[index] || ''}
                        onChange={(e) => {
                          handleStokSearch(index, e.target.value);
                          const newDropdowns = [...showStokDropdowns];
                          newDropdowns[index] = true;
                          setShowStokDropdowns(newDropdowns);
                        }}
                        onFocus={() => {
                          const newDropdowns = [...showStokDropdowns];
                          newDropdowns[index] = true;
                          setShowStokDropdowns(newDropdowns);
                        }}
                        placeholder="Ürün ara..."
                        data-testid={`stok-search-${index}`}
                      />
                      {showStokDropdowns[index] && filteredStokList[index]?.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {filteredStokList[index].slice(0, 10).map((stok) => (
                            <div
                              key={stok.id}
                              className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                              onClick={() => handleSelectStok(index, stok)}
                            >
                              <div className="font-medium">{stok.product_name}{stok.model ? ` - ${stok.model}` : ''}</div>
                              <div className="text-xs text-gray-500">Birim: {stok.unit || 'adet'}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="w-20">
                      <Label className="text-xs">Miktar</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                        placeholder="0"
                        data-testid={`quantity-${index}`}
                      />
                    </div>
                    <div className="w-16">
                      <Label className="text-xs">Birim</Label>
                      <Input
                        value={item.unit}
                        disabled
                        className="bg-gray-100 text-center"
                      />
                    </div>
                    <div className="w-28">
                      <Label className="text-xs text-emerald-600">Birim Fiyat ({({'TL':'₺','USD':'$','EUR':'€','GBP':'£'})[formData.currency] || '₺'})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.purchase_price}
                        onChange={(e) => handleItemPriceChange(index, e.target.value)}
                        placeholder="0.00"
                        className="text-right"
                        data-testid={`purchase-price-${index}`}
                      />
                    </div>
                    <div className="w-20">
                      <Label className="text-xs">KDV %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={item.kdv_orani ?? 20}
                        onChange={(e) => {
                          const newItems = [...formData.items];
                          newItems[index] = { ...newItems[index], kdv_orani: parseFloat(e.target.value) || 0 };
                          setFormData(prev => ({ ...prev, items: newItems }));
                        }}
                        className="text-center"
                        data-testid={`kdv-${index}`}
                      />
                    </div>
                    {formData.items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(index)}
                        className="mt-5 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {/* Ürün Toplamı */}
              {formData.items.some(i => parseFloat(i.purchase_price) > 0) && (
                <div className="flex flex-col items-end gap-1 pt-2 mt-2 border-t">
                  <span className="text-sm text-gray-500">
                    Ara Toplam: {formData.items.reduce((sum, i) => sum + ((parseFloat(i.quantity) || 0) * (parseFloat(i.purchase_price) || 0)), 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} {({'TL':'₺','USD':'$','EUR':'€','GBP':'£'})[formData.currency] || '₺'}
                  </span>
                  <span className="text-sm text-gray-500">
                    KDV Toplamı: {formData.items.reduce((sum, i) => sum + ((parseFloat(i.quantity) || 0) * (parseFloat(i.purchase_price) || 0) * ((i.kdv_orani || 0) / 100)), 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} {({'TL':'₺','USD':'$','EUR':'€','GBP':'£'})[formData.currency] || '₺'}
                  </span>
                </div>
              )}
            </div>

            {/* Nakliye Ücreti */}
            <div className="space-y-3 p-4 border rounded-lg bg-blue-50">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="order-nakliye-toggle">
                <input
                  type="checkbox"
                  checked={formData.nakliye_var}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    nakliye_var: e.target.checked,
                    nakliye_ucreti: e.target.checked ? prev.nakliye_ucreti : ''
                  }))}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Truck className="h-5 w-5 text-blue-600" />
                <span className="text-base font-semibold text-blue-800">Nakliye Ücreti Var</span>
              </label>
              {formData.nakliye_var && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-blue-700 whitespace-nowrap">Nakliye Tutarı:</Label>
                  <div className="relative w-48">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">{({'TL':'₺','USD':'$','EUR':'€','GBP':'£'})[formData.currency] || '₺'}</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.nakliye_ucreti}
                      onChange={(e) => setFormData(prev => ({ ...prev, nakliye_ucreti: e.target.value }))}
                      placeholder="0.00"
                      className="pl-7"
                      data-testid="order-nakliye-amount"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Genel Toplam Özeti */}
            {formData.items.some(i => parseFloat(i.purchase_price) > 0) && (() => {
              const sym = ({'TL':'₺','USD':'$','EUR':'€','GBP':'£'})[formData.currency] || '₺';
              const subtotal = formData.items.reduce((s, i) => s + ((parseFloat(i.quantity)||0) * (parseFloat(i.purchase_price)||0)), 0);
              const kdvTotal = formData.items.reduce((s, i) => s + ((parseFloat(i.quantity)||0) * (parseFloat(i.purchase_price)||0) * ((i.kdv_orani||0)/100)), 0);
              const nakliye = formData.nakliye_var ? (parseFloat(formData.nakliye_ucreti) || 0) : 0;
              const total = subtotal + kdvTotal + nakliye;
              return (
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm text-gray-500">Ara Toplam: {subtotal.toLocaleString('tr-TR', {minimumFractionDigits: 2})} {sym}</span>
                    <span className="text-sm text-gray-500">KDV Toplamı: {kdvTotal.toLocaleString('tr-TR', {minimumFractionDigits: 2})} {sym}</span>
                    {nakliye > 0 && <span className="text-sm text-blue-600">Nakliye: {nakliye.toLocaleString('tr-TR', {minimumFractionDigits: 2})} {sym}</span>}
                    <span className="font-bold text-lg border-t pt-1 mt-1" data-testid="order-grand-total">
                      Genel Toplam: {total.toLocaleString('tr-TR', {minimumFractionDigits: 2})} {sym}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Açıklama */}
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Sipariş hakkında not..."
                rows={2}
                data-testid="order-description"
              />
            </div>

            {/* Ödeme Planı */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Ödeme Planı</Label>
              </div>
              <div className="flex gap-2 mb-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    payment_plan: [...prev.payment_plan, { type: 'cek', date: '', amount: '', description: '' }]
                  }))}
                  className="gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="add-check-payment"
                >
                  <CreditCard className="h-4 w-4" />
                  Çek Ekle
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    payment_plan: [...prev.payment_plan, { type: 'nakit', date: '', amount: '', description: '' }]
                  }))}
                  className="gap-1 border-green-300 text-green-700 hover:bg-green-50"
                  data-testid="add-cash-payment"
                >
                  <DollarSign className="h-4 w-4" />
                  Nakit Ekle
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    payment_plan: [...prev.payment_plan, { type: 'kredi_karti', date: '', amount: '', description: '' }]
                  }))}
                  className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="add-credit-payment"
                >
                  <CreditCard className="h-4 w-4" />
                  Kredi Kartı Ekle
                </Button>
              </div>
              {formData.payment_plan.length > 0 ? (
                <div className="space-y-2">
                  {formData.payment_plan.map((payment, idx) => (
                    <div key={idx} className={`flex gap-2 items-center p-3 rounded-lg border ${
                      payment.type === 'cek' ? 'bg-purple-50 border-purple-200' :
                      payment.type === 'nakit' ? 'bg-green-50 border-green-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <Badge variant="secondary" className={`text-xs whitespace-nowrap ${
                        payment.type === 'cek' ? 'bg-purple-100 text-purple-700' :
                        payment.type === 'nakit' ? 'bg-green-100 text-green-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {payment.type === 'cek' ? 'Çek' : payment.type === 'nakit' ? 'Nakit' : 'Kredi Kartı'}
                      </Badge>
                      <div className="w-36">
                        <Input
                          type="date"
                          value={payment.date}
                          onChange={(e) => {
                            const updated = [...formData.payment_plan];
                            updated[idx] = { ...updated[idx], date: e.target.value };
                            setFormData(prev => ({ ...prev, payment_plan: updated }));
                          }}
                          className="text-sm"
                          data-testid={`payment-date-${idx}`}
                        />
                      </div>
                      <div className="w-32">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Tutar (₺)"
                          value={payment.amount}
                          onChange={(e) => {
                            const updated = [...formData.payment_plan];
                            updated[idx] = { ...updated[idx], amount: e.target.value };
                            setFormData(prev => ({ ...prev, payment_plan: updated }));
                          }}
                          className="text-sm text-right"
                          data-testid={`payment-amount-${idx}`}
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          placeholder="Açıklama"
                          value={payment.description}
                          onChange={(e) => {
                            const updated = [...formData.payment_plan];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            setFormData(prev => ({ ...prev, payment_plan: updated }));
                          }}
                          className="text-sm"
                          data-testid={`payment-desc-${idx}`}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const updated = formData.payment_plan.filter((_, i) => i !== idx);
                          setFormData(prev => ({ ...prev, payment_plan: updated }));
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-end pt-1">
                    <span className="text-sm font-semibold text-gray-700">
                      Toplam: {formData.payment_plan.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0).toLocaleString('tr-TR')} ₺
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-3 text-gray-400 text-sm border border-dashed rounded-lg">
                  Ödeme planı eklenmedi
                </div>
              )}
            </div>

            {/* Belgeler - Zorunlu */}
            <div>
              <Label className="flex items-center gap-1">
                Belgeler <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500">(En az 1 belge zorunlu)</span>
              </Label>
              <div className={`border-2 border-dashed rounded-lg p-4 mt-2 ${formData.documents.length === 0 ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="order-files"
                />
                <label htmlFor="order-files" className="flex flex-col items-center cursor-pointer">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Dosya yüklemek için tıklayın</span>
                </label>
              </div>
              {formData.documents.length > 0 && (
                <div className="space-y-2 mt-2">
                  {formData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(index)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleAddOrder} 
              disabled={submitting}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="submit-order-btn"
            >
              {submitting ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Ekleniyor...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Sipariş Ekle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Demirbaş Modal */}
      <Dialog open={showDemirbasModal} onOpenChange={setShowDemirbasModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-purple-600" />
              Yeni Demirbaş
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Ürün Adı *</Label>
              <Input
                value={demirbasFormData.product_name}
                onChange={(e) => setDemirbasFormData({ ...demirbasFormData, product_name: e.target.value })}
                placeholder="Demirbaş adı"
                data-testid="demirbas-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Miktar</Label>
                <Input
                  type="number"
                  min="1"
                  value={demirbasFormData.quantity}
                  onChange={(e) => setDemirbasFormData({ ...demirbasFormData, quantity: parseFloat(e.target.value) || 1 })}
                  data-testid="demirbas-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Birim</Label>
                <Select
                  value={demirbasFormData.unit}
                  onValueChange={(value) => setDemirbasFormData({ ...demirbasFormData, unit: value })}
                >
                  <SelectTrigger data-testid="demirbas-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="adet">Adet</SelectItem>
                    <SelectItem value="set">Set</SelectItem>
                    <SelectItem value="takım">Takım</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="metre">Metre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Alış Fiyatı (₺)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={demirbasFormData.purchase_price}
                onChange={(e) => setDemirbasFormData({ ...demirbasFormData, purchase_price: parseFloat(e.target.value) || 0 })}
                data-testid="demirbas-price-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Marka</Label>
                <Input
                  value={demirbasFormData.brand}
                  onChange={(e) => setDemirbasFormData({ ...demirbasFormData, brand: e.target.value })}
                  placeholder="Ürün markası"
                  data-testid="demirbas-brand-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Cari Hesap</Label>
                <Select
                  value={demirbasFormData.supplier_id}
                  onValueChange={(value) => {
                    const selectedCari = demirbasCariList.find(c => c.id === value);
                    setDemirbasFormData({ 
                      ...demirbasFormData, 
                      supplier_id: value,
                      supplier: selectedCari?.firma_adi || selectedCari?.ad || ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="demirbas-supplier-select">
                    <SelectValue placeholder="Cari hesap seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {demirbasCariList.map((cari) => (
                      <SelectItem key={cari.id} value={cari.id}>
                        {cari.firma_adi || cari.ad} {cari.firma_adi && cari.ad ? `(${cari.ad})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Seri Numarası</Label>
              <Input
                value={demirbasFormData.serial_number}
                onChange={(e) => setDemirbasFormData({ ...demirbasFormData, serial_number: e.target.value })}
                placeholder="Seri/Model numarası"
                data-testid="demirbas-serial-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Durum</Label>
              <Select
                value={demirbasFormData.status}
                onValueChange={(value) => setDemirbasFormData({ ...demirbasFormData, status: value })}
              >
                <SelectTrigger data-testid="demirbas-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="maintenance">Bakımda</SelectItem>
                  <SelectItem value="disposed">Elden Çıkarıldı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={demirbasFormData.description}
                onChange={(e) => setDemirbasFormData({ ...demirbasFormData, description: e.target.value })}
                placeholder="Ek açıklamalar..."
                rows={2}
                data-testid="demirbas-description-input"
              />
            </div>

            {/* Fatura Belgesi Bölümü */}
            <div className="space-y-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <Label className="text-orange-700 font-medium">Fatura Belgesi</Label>
              <div className="border-2 border-dashed border-orange-300 rounded-lg p-3 bg-white">
                <input
                  type="file"
                  multiple
                  onChange={handleDemirbasInvoiceUpload}
                  className="hidden"
                  id="demirbas-invoice-files"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <label htmlFor="demirbas-invoice-files" className="flex flex-col items-center cursor-pointer">
                  <Upload className="h-6 w-6 text-orange-400 mb-1" />
                  <span className="text-sm text-orange-600">Fatura yükle</span>
                  <span className="text-xs text-orange-400 mt-1">PDF, Word, Resim</span>
                </label>
              </div>
              
              {demirbasFormData.invoice_documents.length > 0 && (
                <div className="space-y-2 mt-2">
                  {demirbasFormData.invoice_documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border border-orange-200">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-orange-500 flex-shrink-0" />
                        <span className="text-sm truncate max-w-[180px]">{doc.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDemirbasFormData(prev => ({
                          ...prev,
                          invoice_documents: prev.invoice_documents.filter((_, i) => i !== index)
                        }))}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Belgeler Bölümü */}
            <div className="space-y-2">
              <Label>Diğer Belgeler</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleDemirbasFileUpload}
                  className="hidden"
                  id="demirbas-files"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <label htmlFor="demirbas-files" className="flex flex-col items-center cursor-pointer">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Dosya yüklemek için tıklayın</span>
                  <span className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Resim</span>
                </label>
              </div>
              
              {demirbasFormData.documents.length > 0 && (
                <div className="space-y-2 mt-2">
                  {demirbasFormData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDemirbasFormData(prev => ({
                          ...prev,
                          documents: prev.documents.filter((_, i) => i !== index)
                        }))}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDemirbasModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleDemirbasSubmit} 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={submitting}
              data-testid="demirbas-submit-btn"
            >
              {submitting ? 'Ekleniyor...' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedItem?._type === 'defective' ? (
                <>
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Eksik/Hatalı Ürün Detayı
                </>
              ) : (
                <>
                  <Package className="h-5 w-5 text-orange-600" />
                  Sipariş Detayı
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (selectedItem._type === 'defective' || selectedItem._type === 'defective_group') ? (
            // Defective product detail (single item or group)
            <div className="space-y-4">
              {/* If it's a group, show item list */}
              {selectedItem._type === 'defective_group' && selectedItem.items && selectedItem.items.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">Şube</Label>
                      <p className="font-medium">{selectedItem.branch_name || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Tedarikçi</Label>
                      <p className="font-medium">{selectedItem.cari_hesap || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Toplam Ürün</Label>
                      <p className="font-medium">{selectedItem.item_count || selectedItem.items.length} Kalem</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Kayıt Tarihi</Label>
                      <p className="font-medium">{formatDateTime(selectedItem.created_at)}</p>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-red-50 px-3 py-2 border-b">
                      <span className="text-sm font-medium text-red-800">Eksik/Hatalı Ürünler</span>
                    </div>
                    <div className="max-h-80 overflow-y-auto overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-xs">Ürün Adı</TableHead>
                            <TableHead className="text-xs text-center">Beklenen</TableHead>
                            <TableHead className="text-xs text-center">İrsaliye</TableHead>
                            <TableHead className="text-xs text-center">Teslim Alınan</TableHead>
                            <TableHead className="text-xs text-center">Eksik</TableHead>
                            <TableHead className="text-xs">Sorun Tipi</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedItem.items.map((item, idx) => {
                            const hasMismatch = item.irsaliye_quantity != null && item.irsaliye_quantity !== '' &&
                              item.received_quantity != null &&
                              parseFloat(item.irsaliye_quantity) !== parseFloat(item.received_quantity);
                            return (
                              <React.Fragment key={item.id || idx}>
                                <TableRow data-testid={`defective-item-${idx}`}>
                                  <TableCell className="text-sm">{item.product_name}</TableCell>
                                  <TableCell className="text-sm text-center text-gray-600" data-testid={`defective-expected-${idx}`}>
                                    {item.expected_quantity != null ? `${item.expected_quantity} ${item.unit || ''}` : '-'}
                                  </TableCell>
                                  <TableCell className={cn("text-sm text-center", hasMismatch ? "text-amber-700 font-semibold" : "text-gray-600")} data-testid={`defective-irsaliye-${idx}`}>
                                    {item.irsaliye_quantity != null && item.irsaliye_quantity !== '' ? `${item.irsaliye_quantity} ${item.unit || ''}` : '-'}
                                  </TableCell>
                                  <TableCell className={cn("text-sm text-center", hasMismatch ? "text-amber-700 font-semibold" : "text-gray-600")} data-testid={`defective-received-${idx}`}>
                                    {item.received_quantity != null ? `${item.received_quantity} ${item.unit || ''}` : '-'}
                                  </TableCell>
                                  <TableCell className="text-sm text-center text-red-600 font-medium">{item.quantity} {item.unit}</TableCell>
                                  <TableCell className="text-sm">{getIssueTypeBadge(item.issue_type)}</TableCell>
                                </TableRow>
                                {(hasMismatch || item.aciklama || (item.description && item.hata_cikma_zamani === 'teslim_alirken')) && (
                                  <TableRow className="bg-amber-50/50">
                                    <TableCell colSpan={6} className="text-xs py-2">
                                      {hasMismatch && (
                                        <div className="flex items-center gap-1 text-amber-700 font-medium mb-1">
                                          <AlertTriangle className="h-3 w-3" />
                                          İrsaliye ({item.irsaliye_quantity} {item.unit}) ile teslim alınan ({item.received_quantity} {item.unit}) tutmuyor
                                        </div>
                                      )}
                                      {(item.aciklama || item.description) && (
                                        <div className="text-gray-600">
                                          <span className="font-medium text-gray-500">Depo Notu: </span>
                                          {item.aciklama || item.description}
                                        </div>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              ) : (
                // Single defective item
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Ürün Adı</Label>
                    <p className="font-medium">{selectedItem.product_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Sorun Tipi</Label>
                    <div className="mt-1">{getIssueTypeBadge(selectedItem.issue_type)}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Şube</Label>
                    <p className="font-medium">{selectedItem.branch_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Miktar (Eksik)</Label>
                    <p className="font-medium text-red-600">{selectedItem.quantity} {selectedItem.unit}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Tedarikçi</Label>
                    <p className="font-medium">{selectedItem.cari_hesap || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Durum</Label>
                    <div className="mt-1">{getDefectiveStatusBadge(selectedItem.status)}</div>
                  </div>
                  {selectedItem.expected_quantity != null && (
                    <div>
                      <Label className="text-gray-500">Beklenen Miktar</Label>
                      <p className="font-medium text-gray-700">{selectedItem.expected_quantity} {selectedItem.unit}</p>
                    </div>
                  )}
                  {selectedItem.irsaliye_quantity != null && selectedItem.irsaliye_quantity !== '' && (
                    <div>
                      <Label className="text-gray-500">İrsaliyede Yazan</Label>
                      <p className="font-medium text-gray-700">{selectedItem.irsaliye_quantity} {selectedItem.unit}</p>
                    </div>
                  )}
                  {selectedItem.received_quantity != null && (
                    <div>
                      <Label className="text-gray-500">Teslim Alınan</Label>
                      <p className="font-medium text-gray-700">{selectedItem.received_quantity} {selectedItem.unit}</p>
                    </div>
                  )}
                </div>
              )}

              {/* İrsaliye-Teslim alınan uyuşmazlık uyarısı (tekil) */}
              {selectedItem._type === 'defective' && selectedItem.irsaliye_quantity != null && selectedItem.irsaliye_quantity !== '' &&
                selectedItem.received_quantity != null &&
                parseFloat(selectedItem.irsaliye_quantity) !== parseFloat(selectedItem.received_quantity) && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  İrsaliyede yazan ({selectedItem.irsaliye_quantity} {selectedItem.unit}) ile teslim alınan ({selectedItem.received_quantity} {selectedItem.unit}) miktar uyuşmuyor.
                </div>
              )}
              
              {selectedItem.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="mt-1 p-3 bg-red-50 rounded-lg text-red-800">{selectedItem.description}</p>
                </div>
              )}
              
              {/* Action Buttons for defective items */}
              {selectedItem.has_open_issues !== false && selectedItem.status !== 'iptal' && (
                <div className="pt-4 border-t flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => openCancelModal(selectedItem)}
                  >
                    <X className="h-4 w-4 mr-2" />
                    İptal Et
                  </Button>
                  <Button
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                    onClick={() => openCompleteOrderModal(selectedItem)}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Tamamlanacak Ürünler
                  </Button>
                </div>
              )}
              
              {selectedItem.cancel_reason && (
                <div>
                  <Label className="text-gray-500">İptal Nedeni</Label>
                  <p className="mt-1 p-3 bg-gray-50 rounded-lg text-gray-800">{selectedItem.cancel_reason}</p>
                </div>
              )}
            </div>
          ) : selectedItem && (
            // Order detail
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedItem.status)}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Tedarikçi (Cari Hesap)</Label>
                  <p className="font-medium">{selectedItem.cari_hesap_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Şube</Label>
                  <p className="font-medium">{selectedItem.branch_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Sipariş Tarihi</Label>
                  <p className="font-medium">{formatDate(selectedItem.order_date || selectedItem.entry_datetime || selectedItem.created_at)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Beklenen Tarih</Label>
                  <p className="font-medium">{formatDate(selectedItem.expected_date || selectedItem.estimated_arrival_datetime)}</p>
                </div>
                {selectedItem.arrived_at && (
                  <div>
                    <Label className="text-gray-500">Geldiği Tarih</Label>
                    <p className="font-medium">{formatDate(selectedItem.arrived_at)}</p>
                  </div>
                )}
                {selectedItem.source_job_seri_no && (
                  <div>
                    <Label className="text-gray-500">Kaynak İş No</Label>
                    <p className="font-medium">{selectedItem.source_job_seri_no}</p>
                  </div>
                )}
              </div>

              {/* Ürün Detayları Tablosu */}
              <div>
                <Label className="text-base font-semibold flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-orange-600" />
                  Ürün Detayları ({(selectedItem.items || []).length} Kalem)
                </Label>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs">Ürün</TableHead>
                        <TableHead className="text-xs">Miktar</TableHead>
                        <TableHead className="text-xs text-right">Birim Fiyat</TableHead>
                        <TableHead className="text-xs text-right">KDV</TableHead>
                        <TableHead className="text-xs text-right">Satır Toplam</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedItem.items || []).map((it, idx) => {
                        const qty = parseFloat(it.quantity) || 0;
                        const price = parseFloat(it.purchase_price) || 0;
                        const kdv = it.kdv_orani != null ? parseFloat(it.kdv_orani) : 20;
                        const lineTotal = qty * price * (1 + kdv / 100);
                        return (
                          <TableRow key={it.id || idx} data-testid={`order-detail-item-${idx}`}>
                            <TableCell className="text-sm">
                              <span className="font-medium">{it.product_name || '-'}</span>
                              {it.model && <span className="text-gray-400 block text-xs">{it.model}</span>}
                            </TableCell>
                            <TableCell className="text-sm">{qty} {it.unit || ''}</TableCell>
                            <TableCell className="text-sm text-right">{price.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</TableCell>
                            <TableCell className="text-sm text-right">%{kdv}</TableCell>
                            <TableCell className="text-sm text-right font-medium">{lineTotal.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {/* Toplamlar */}
                {(() => {
                  const items = selectedItem.items || [];
                  const sub = items.reduce((s, it) => s + (parseFloat(it.quantity)||0) * (parseFloat(it.purchase_price)||0), 0);
                  const kdvT = items.reduce((s, it) => s + (parseFloat(it.quantity)||0) * (parseFloat(it.purchase_price)||0) * (((it.kdv_orani!=null?parseFloat(it.kdv_orani):20))/100), 0);
                  const grand = sub + kdvT;
                  return (
                    <div className="flex flex-col items-end gap-0.5 mt-2 pr-1">
                      <span className="text-sm text-gray-500">Ara Toplam: {sub.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</span>
                      <span className="text-sm text-gray-500">KDV Toplamı: {kdvT.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</span>
                      <span className="font-bold text-lg border-t pt-1 mt-1" data-testid="order-detail-grand-total">Genel Toplam: {grand.toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</span>
                    </div>
                  );
                })()}
              </div>

              {/* Ödeme Koşulları */}
              {selectedItem.payment_plan && selectedItem.payment_plan.length > 0 && (
                <div>
                  <Label className="text-base font-semibold flex items-center gap-2 mb-2">
                    <CreditCard className="h-4 w-4 text-blue-600" />
                    Ödeme Koşulları
                  </Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-xs">Ödeme Şekli</TableHead>
                          <TableHead className="text-xs">Tarih</TableHead>
                          <TableHead className="text-xs">Açıklama</TableHead>
                          <TableHead className="text-xs text-right">Tutar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedItem.payment_plan.map((p, idx) => (
                          <TableRow key={idx} data-testid={`order-detail-payment-${idx}`}>
                            <TableCell className="text-sm">
                              <Badge variant="outline" className="text-xs">
                                {p.type === 'cek' ? 'Çek' : p.type === 'kredi_karti' ? 'Kredi Kartı' : 'Nakit'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{p.date ? formatDate(p.date) : '-'}</TableCell>
                            <TableCell className="text-sm">{p.description || '-'}</TableCell>
                            <TableCell className="text-sm text-right font-medium">{(parseFloat(p.amount)||0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex justify-end mt-1 pr-1">
                    <span className="text-sm font-semibold text-blue-700">Ödeme Toplamı: {selectedItem.payment_plan.reduce((s,p) => s + (parseFloat(p.amount)||0), 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ₺</span>
                  </div>
                </div>
              )}

              {/* Onaylayanlar */}
              {selectedItem.approvers && selectedItem.approvers.length > 0 && (
                <div>
                  <Label className="text-base font-semibold flex items-center gap-2 mb-2">
                    <UserCheck className="h-4 w-4 text-emerald-600" />
                    Onaylayanlar
                  </Label>
                  <div className="border rounded-lg divide-y">
                    {selectedItem.approvers.map((a, idx) => (
                      <div key={idx} className="flex items-center justify-between px-3 py-2" data-testid={`order-detail-approver-${idx}`}>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                          <div>
                            <p className="text-sm font-medium">{a.name || '-'}</p>
                            <p className="text-xs text-gray-500">{a.role}</p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">{a.at ? formatDateTime(a.at) : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedItem.notes && (
                <div>
                  <Label className="text-gray-500">Notlar</Label>
                  <p className="mt-1 p-3 bg-gray-50 rounded-lg">{selectedItem.notes}</p>
                </div>
              )}
              {selectedItem.documents && selectedItem.documents.length > 0 && (
                <div>
                  <Label className="text-gray-500">Belgeler ({selectedItem.documents.length})</Label>
                  <div className="mt-2 space-y-1">
                    {selectedItem.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{doc.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.data && (
                            <>
                              <a href={doc.data} target="_blank" rel="noopener noreferrer">
                                <Button variant="ghost" size="sm" className="text-blue-600">
                                  <Eye className="h-3 w-3 mr-1" />
                                  Görüntüle
                                </Button>
                              </a>
                              <a 
                                href={doc.data} 
                                download={doc.name || `belge-${idx + 1}`}
                              >
                                <Button variant="ghost" size="sm" className="text-green-600">
                                  <Download className="h-3 w-3 mr-1" />
                                  İndir
                                </Button>
                              </a>
                            </>
                          )}
                          {doc.url && !doc.data && (
                            <>
                              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                <Button variant="ghost" size="sm" className="text-blue-600">
                                  <Eye className="h-3 w-3 mr-1" />
                                  Görüntüle
                                </Button>
                              </a>
                              <a 
                                href={doc.url} 
                                download={doc.name || `belge-${idx + 1}`}
                              >
                                <Button variant="ghost" size="sm" className="text-green-600">
                                  <Download className="h-3 w-3 mr-1" />
                                  İndir
                                </Button>
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="h-5 w-5" />
              Kaydı İptal Et
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              Bu kaydı iptal etmek istediğinize emin misiniz? İptal edilen kayıtlar "İptal Edilenler" sekmesine taşınacaktır.
            </p>
            {cancellingItem && (
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>Toplam:</strong> {cancellingItem.item_count || cancellingItem.items?.length || 1} kalem ürün iptal edilecek
                </p>
              </div>
            )}
            <div>
              <Label>İptal Nedeni <span className="text-red-500">*</span></Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="İptal nedenini yazın..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Vazgeç
            </Button>
            <Button 
              onClick={handleCancelDefective}
              disabled={!cancelReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              <X className="h-4 w-4 mr-2" />
              İptal Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Order Modal - Tamamlanacak Ürünler */}
      <Dialog open={showCompleteOrderModal} onOpenChange={setShowCompleteOrderModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Package className="h-5 w-5" />
              Tamamlanacak Ürünler
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              Eksik ürünlerden kaç adet tamamlanacağını girin. Sipariş "Sipariş Edilen" sekmesine eklenecektir.
            </p>
            
            {/* Cari Hesap and Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tedarikçi (Cari Hesap) <span className="text-xs text-gray-500">(Otomatik)</span></Label>
                <Select value={completeOrderCariId} onValueChange={setCompleteOrderCariId}>
                  <SelectTrigger className={completeOrderCariId ? "border-green-300 bg-green-50" : ""}>
                    <SelectValue placeholder="Tedarikçi seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {cariHesaplar.map(cari => (
                      <SelectItem key={cari.id} value={cari.id}>{cari.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tahmini Varış Tarihi</Label>
                <Input 
                  type="datetime-local" 
                  value={completeOrderDate}
                  onChange={(e) => setCompleteOrderDate(e.target.value)}
                />
              </div>
            </div>
            
            {/* Items Table */}
            {completeOrderItem && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-orange-50 px-3 py-2 border-b">
                  <span className="text-sm font-medium text-orange-800">Eksik Ürünler</span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs">Ürün Adı</TableHead>
                      <TableHead className="text-xs">Eksik Miktar</TableHead>
                      <TableHead className="text-xs">Birim</TableHead>
                      <TableHead className="text-xs w-40">Tamamlanacak Eksik</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {completeOrderItem.items ? completeOrderItem.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="text-sm font-medium">{item.product_name}</TableCell>
                        <TableCell className="text-sm text-red-600 font-semibold">{item.quantity} {item.unit}</TableCell>
                        <TableCell className="text-sm">{item.unit}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            max={item.quantity}
                            value={completeOrderQuantities[item.id] ?? ''}
                            onChange={(e) => setCompleteOrderQuantities(prev => ({
                              ...prev,
                              [item.id]: e.target.value === '' ? '' : parseInt(e.target.value) || 0
                            }))}
                            className="w-28 text-center"
                            placeholder="Miktar girin"
                          />
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell className="text-sm font-medium">{completeOrderItem.product_name}</TableCell>
                        <TableCell className="text-sm text-red-600 font-semibold">{completeOrderItem.quantity} {completeOrderItem.unit}</TableCell>
                        <TableCell className="text-sm">{completeOrderItem.unit}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            max={completeOrderItem.quantity}
                            value={completeOrderQuantities[completeOrderItem.id] ?? ''}
                            onChange={(e) => setCompleteOrderQuantities(prev => ({
                              ...prev,
                              [completeOrderItem.id]: e.target.value === '' ? '' : parseInt(e.target.value) || 0
                            }))}
                            className="w-28 text-center"
                            placeholder="Miktar girin"
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
            
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
              <strong>Not:</strong> Girdiğiniz miktarlar "Sipariş Edilen" sekmesine yeni sipariş olarak eklenecektir.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCompleteOrderModal(false)}>
              Vazgeç
            </Button>
            <Button 
              onClick={handleCompleteOrder}
              disabled={submitting || Object.values(completeOrderQuantities).every(q => !q || q <= 0)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Ekleniyor...
                </>
              ) : (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  Siparişi Oluştur
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasingPurchased;
